import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Topo from './components/Topo';
import PassoIdentificar from './components/PassoIdentificar';
import PassoFilhos from './components/PassoFilhos';
import PassoTermo from './components/PassoTermo';
import PassoPronto from './components/PassoPronto';
import RodapeFixo from './components/RodapeFixo';
import BotaoPrincipal from './components/BotaoPrincipal';
import {
  dataParaIso,
  dataValida,
  deFilhoDoBanco,
  novaCrianca,
  primeiroNome,
  remediosParaTexto,
} from './utils';
import type { BuscaResposta, Crianca, FilhoParaSalvar, SalvarResposta } from './types';

type DestaqueTermo = 'aceite' | 'aceite_nome' | null;

const MSG_REDE = 'Não consegui abrir agora. Verifique a internet e tente de novo.';

// Campo editado → campo de erro que ele resolve.
const CAMPOS_DE_ERRO: Record<string, string> = {
  nome: 'nome',
  nascimento: 'nascimento',
  temAlergia: 'alergia',
  alergiaTexto: 'alergia',
  temRestricao: 'alimentacao',
  alimentacaoTexto: 'alimentacao',
  tomaRemedio: 'remedios',
  remedios: 'remedios',
  autorizaFebre: 'febre',
  febreTexto: 'febre',
};

function irPara(id: string) {
  window.setTimeout(() => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 80);
}

export default function CriancasPage() {
  const { eventoId } = useParams<{ eventoId: string }>();

  const eventoParam = useMemo(() => {
    const numero = Number(eventoId);
    return Number.isFinite(numero) && eventoId ? numero : eventoId ?? '';
  }, [eventoId]);

  const [passo, setPasso] = useState(1);
  const [cpf, setCpf] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState<string | null>(null);
  const [bloqueado, setBloqueado] = useState<string | null>(null);

  const [dados, setDados] = useState<BuscaResposta | null>(null);
  const [criancas, setCriancas] = useState<Crianca[]>([]);
  const [contato, setContato] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [destaqueCrianca, setDestaqueCrianca] = useState<number | null>(null);

  const [aceito, setAceito] = useState(false);
  const [nomeAssinatura, setNomeAssinatura] = useState('');
  const [destaqueTermo, setDestaqueTermo] = useState<DestaqueTermo>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const [pronto, setPronto] = useState<{ mensagem: string; registro?: string } | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [passo]);

  const subtitulo = useMemo(() => {
    const evento = dados?.evento;
    if (!evento) return undefined;
    return [evento.nome, evento.quando, evento.local].filter(Boolean).join(' · ');
  }, [dados]);

  const destaques = useMemo(() => {
    if (destaqueCrianca === null) return {} as Record<string, boolean>;
    const crianca = criancas[destaqueCrianca];
    return crianca ? { [crianca.chave]: true } : ( {} as Record<string, boolean>);
  }, [criancas, destaqueCrianca]);

  async function buscar() {
    if (buscando) return;
    const digitos = cpf.replace(/\D/g, '');
    if (digitos.length !== 11) {
      setErroBusca('Digite o CPF completo, com 11 números.');
      return;
    }
    if (!dataValida(nascimento)) {
      setErroBusca('Confira a data de nascimento: dia/mês/ano.');
      return;
    }

    setBuscando(true);
    setErroBusca(null);
    try {
      const { data, error } = await supabase.rpc('filhos_acampamento_buscar', {
        p_evento: eventoParam,
        p_cpf: digitos,
        p_nascimento: dataParaIso(nascimento),
      });
      if (error) throw error;

      const resposta = (data ?? null) as BuscaResposta | null;
      if (!resposta) {
        setErroBusca(MSG_REDE);
        return;
      }
      if (resposta.ok === false) {
        const mensagem = resposta.mensagem || MSG_REDE;
        if (resposta.motivo === 'encerrado') {
          setBloqueado(mensagem);
        } else {
          setErroBusca(mensagem);
        }
        return;
      }

      setDados(resposta);
      const doBanco = Array.isArray(resposta.filhos) ? resposta.filhos : [];
      setCriancas(doBanco.length ? doBanco.map(deFilhoDoBanco) : [novaCrianca()]);
      setContato(resposta.contato_emergencia ?? '');
      setErros({});
      setErroBusca(null);
      setPasso(2);
    } catch {
      setErroBusca(MSG_REDE);
    } finally {
      setBuscando(false);
    }
  }

  function mudarCrianca(indice: number, patch: Partial<Crianca>) {
    setCriancas((atual) =>
      atual.map((crianca, i) => (i === indice ? { ...crianca, ...patch } : crianca)),
    );

    const crianca = criancas[indice];
    if (!crianca) return;

    // Corrigiu o campo? O erro daquele campo sai na hora.
    setErros((atual) => {
      const proximo = { ...atual };
      Object.keys(patch).forEach((campo) => {
        const chaveErro = CAMPOS_DE_ERRO[campo];
        if (chaveErro) delete proximo[`${crianca.chave}-${chaveErro}`];
      });
      return proximo;
    });
    setDestaqueCrianca(null);
    setErroSalvar(null);
  }

  function adicionarCrianca() {
    setCriancas((atual) => (atual.length >= 6 ? atual : [...atual, novaCrianca()]));
  }

  function removerCrianca(indice: number) {
    setCriancas((atual) => (atual.length <= 1 ? atual : atual.filter((_, i) => i !== indice)));
    setErros({});
  }

  function validarCriancas(): { mapa: Record<string, string>; primeiroId: string | null } {
    const mapa: Record<string, string> = {};
    let primeiroId: string | null = null;

    const marcar = (chave: string, id: string, mensagem: string) => {
      mapa[chave] = mensagem;
      if (!primeiroId) primeiroId = id;
    };

    criancas.forEach((crianca, indice) => {
      const n = indice + 1;
      if (!crianca.nome.trim()) {
        marcar(`${crianca.chave}-nome`, `nome-crianca-${n}`, 'Falta o nome completo.');
      }
      if (!crianca.nascimento.trim()) {
        marcar(
          `${crianca.chave}-nascimento`,
          `nascimento-crianca-${n}`,
          'Falta a data de nascimento.',
        );
      } else if (!dataValida(crianca.nascimento)) {
        marcar(
          `${crianca.chave}-nascimento`,
          `nascimento-crianca-${n}`,
          'Confira a data: dia/mês/ano.',
        );
      }

      if (crianca.temAlergia === null) {
        marcar(`${crianca.chave}-alergia`, `crianca-${n}`, 'Responda sobre alergias.');
      } else if (crianca.temAlergia && !crianca.alergiaTexto.trim()) {
        marcar(`${crianca.chave}-alergia`, `crianca-${n}`, 'Diga a que tem alergia.');
      }

      if (crianca.temRestricao === null) {
        marcar(`${crianca.chave}-alimentacao`, `crianca-${n}`, 'Responda sobre alimentação.');
      } else if (crianca.temRestricao && !crianca.alimentacaoTexto.trim()) {
        marcar(
          `${crianca.chave}-alimentacao`,
          `crianca-${n}`,
          'Diga o que não pode comer ou precisa comer.',
        );
      }

      if (crianca.tomaRemedio === null) {
        marcar(`${crianca.chave}-remedios`, `crianca-${n}`, 'Responda sobre remédios.');
      } else if (crianca.tomaRemedio) {
        const validos = crianca.remedios.filter((r) => r.nome.trim());
        if (!validos.length) {
          marcar(
            `${crianca.chave}-remedios`,
            `crianca-${n}`,
            'Escreva o remédio, a dose e os horários.',
          );
        } else if (validos.some((r) => !r.dose.trim() || !r.horarios.trim())) {
          marcar(`${crianca.chave}-remedios`, `crianca-${n}`, 'Complete a dose e os horários.');
        }
      }

      if (crianca.autorizaFebre === null) {
        marcar(
          `${crianca.chave}-febre`,
          `crianca-${n}`,
          'Responda sobre remédio para febre ou dor.',
        );
      } else if (crianca.autorizaFebre && !crianca.febreTexto.trim()) {
        marcar(`${crianca.chave}-febre`, `crianca-${n}`, 'Diga qual remédio e quanto.');
      }
    });

    return { mapa, primeiroId };
  }

  function continuarParaTermo() {
    const { mapa, primeiroId } = validarCriancas();
    setErros(mapa);
    if (primeiroId) {
      irPara(primeiroId);
      return;
    }
    setErroSalvar(null);
    setPasso(3);
  }

  async function enviar() {
    if (enviando) return;

    const faltaTermo = !aceito || nomeAssinatura.trim().length < 3;
    if (faltaTermo) {
      const alvo: DestaqueTermo = !aceito ? 'aceite' : 'aceite_nome';
      setDestaqueTermo(alvo);
      irPara(alvo === 'aceite' ? 'campo-aceite' : 'campo-assinatura');
      return;
    }

    const filhos: FilhoParaSalvar[] = criancas.map((crianca) => ({
      nome: crianca.nome.trim(),
      nascimento: dataParaIso(crianca.nascimento),
      alergias: crianca.temAlergia ? crianca.alergiaTexto.trim() : 'Nenhuma',
      alimentacao: crianca.temRestricao ? crianca.alimentacaoTexto.trim() : 'Come de tudo',
      remedios: crianca.tomaRemedio ? remediosParaTexto(crianca.remedios) : 'Nenhum',
      febre_dor: crianca.autorizaFebre ? `Sim: ${crianca.febreTexto.trim()}` : 'Não autorizado',
      saude: crianca.saude.trim(),
    }));

    setEnviando(true);
    setErroSalvar(null);
    setDestaqueTermo(null);
    setDestaqueCrianca(null);

    try {
      const { data, error } = await supabase.rpc('filhos_acampamento_salvar', {
        p_evento: eventoParam,
        p_cpf: cpf.replace(/\D/g, ''),
        p_nascimento: dataParaIso(nascimento),
        p_filhos: filhos,
        p_contato_emergencia: contato.trim(),
        p_aceite_nome: nomeAssinatura.trim(),
        p_aceito: true,
      });
      if (error) throw error;

      const resposta = (data ?? null) as SalvarResposta | null;
      if (!resposta) {
        setErroSalvar(MSG_REDE);
        return;
      }

      if (resposta.ok === false) {
        setErroSalvar(resposta.mensagem || 'Não consegui enviar agora. Tente de novo.');
        const campo = resposta.campo ?? '';
        const filho = /^filho_(\d+)$/.exec(campo);
        if (filho) {
          const indice = Math.max(0, parseInt(filho[1], 10) - 1);
          setDestaqueCrianca(indice);
          setPasso(2);
          irPara(`crianca-${indice + 1}`);
        } else if (campo === 'filhos') {
          setPasso(2);
          irPara('crianca-1');
        } else if (campo === 'aceite') {
          setDestaqueTermo('aceite');
          irPara('campo-aceite');
        } else if (campo === 'aceite_nome') {
          setDestaqueTermo('aceite_nome');
          irPara('campo-assinatura');
        }
        return;
      }

      setPronto({
        mensagem: resposta.mensagem || 'Recebemos as informações das suas crianças.',
        registro: resposta.registro,
      });
      setPasso(4);
    } catch {
      setErroSalvar(MSG_REDE);
    } finally {
      setEnviando(false);
    }
  }

  function voltar() {
    if (passo === 2) {
      setErroSalvar(null);
      setPasso(1);
    } else if (passo === 3) {
      setErroSalvar(null);
      setPasso(2);
    }
  }

  function fechar() {
    setPasso(1);
    setCpf('');
    setNascimento('');
    setDados(null);
    setCriancas([]);
    setContato('');
    setErros({});
    setDestaqueCrianca(null);
    setAceito(false);
    setNomeAssinatura('');
    setDestaqueTermo(null);
    setErroSalvar(null);
    setErroBusca(null);
    setPronto(null);
  }

  const travado = !aceito || nomeAssinatura.trim().length < 3;
  const comRodape = passo === 2 || passo === 3;

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-background-100">
      <Topo
        subtitulo={subtitulo}
        passo={passo === 2 ? 'Passo 2 de 3' : passo === 3 ? 'Passo 3 de 3' : undefined}
        aoVoltar={passo === 2 || passo === 3 ? voltar : undefined}
      />

      <div
        className={`app-entra mx-auto w-full min-w-0 max-w-[560px] px-4 pt-5 ${
          comRodape ? 'pb-40' : 'pb-10'
        }`}
      >
        {bloqueado ? (
          <div className="rounded-2xl border border-background-200 bg-white p-5 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-time-line text-2xl" aria-hidden />
            </span>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground-800">{bloqueado}</p>
          </div>
        ) : (
          <>
            {erroSalvar && (
              <div className="mb-4 rounded-xl border border-danger-200 bg-danger-50 p-3.5">
                <p className="flex items-start gap-2 text-[14px] leading-relaxed text-danger-700">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    <i className="ri-error-warning-line" aria-hidden />
                  </span>
                  {erroSalvar}
                </p>
              </div>
            )}

            {passo === 1 && (
              <PassoIdentificar
                cpf={cpf}
                nascimento={nascimento}
                erro={erroBusca}
                buscando={buscando}
                aoMudarCpf={setCpf}
                aoMudarNascimento={setNascimento}
                aoContinuar={buscar}
              />
            )}

            {passo === 2 && (
              <PassoFilhos
                primeiroNome={primeiroNome(dados?.primeiro_nome)}
                aceiteRegistrado={dados?.aceite_registrado}
                criancas={criancas}
                erros={erros}
                destaques={destaques}
                aoMudar={mudarCrianca}
                aoRemover={removerCrianca}
                aoAdicionar={adicionarCrianca}
                contato={contato}
                aoMudarContato={setContato}
              />
            )}

            {passo === 3 && (
              <PassoTermo
                termo={dados?.termo ?? ''}
                aceito={aceito}
                nomeAssinatura={nomeAssinatura}
                destaque={destaqueTermo}
                erro={null}
                aoMarcar={(valor) => {
                  setAceito(valor);
                  setDestaqueTermo(null);
                }}
                aoMudarNome={(valor) => {
                  setNomeAssinatura(valor);
                  setDestaqueTermo(null);
                }}
              />
            )}

            {passo === 4 && (
              <PassoPronto
                primeiroNome={primeiroNome(dados?.primeiro_nome)}
                mensagem={pronto?.mensagem ?? ''}
                registro={pronto?.registro}
                aoFechar={fechar}
              />
            )}
          </>
        )}
      </div>

      {passo === 2 && (
        <RodapeFixo>
          <BotaoPrincipal aoTocar={continuarParaTermo}>Continuar para o termo</BotaoPrincipal>
        </RodapeFixo>
      )}

      {passo === 3 && (
        <RodapeFixo>
          <BotaoPrincipal aoTocar={enviar} carregando={enviando} travado={travado}>
            {enviando ? 'Enviando…' : 'Enviar e assinar'}
          </BotaoPrincipal>
        </RodapeFixo>
      )}
    </div>
  );
}